// src/main-component/QRGenerator/index.js
import React, { Fragment, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { 
    Button, 
    TextField, 
    Grid, 
    Paper, 
    Typography, 
    CircularProgress,
    Box,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { QRFirebaseUtils } from '../../utils/firebase';
import { toast } from 'react-toastify';
import Navbar2 from '../../components/Navbar2';
import PageTitle from '../../components/pagetitle';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import Logo from '../../images/logo-nmf.png';
import './qr-generator.scss';

const QRGenerator = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [qrData, setQrData] = useState({
        boxId: '',
        location: '',
        purpose: 'Sadaqah',
        donorName: '',
        donorMobile: '',
        donorPan: '',
        donorEmail: '',
        donorAddress: ''
    });
    const [generatedUrl, setGeneratedUrl] = useState('');
    const [generatedUUID, setGeneratedUUID] = useState('');
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
        setQrData({
            ...qrData,
            [e.target.name]: e.target.value
        });
    };

    const generateQR = async () => {
        // Validation
        if (!qrData.boxId || !qrData.location || !qrData.donorName || !qrData.donorMobile) {
            toast.error('Please fill in required fields: Box ID, Location, Donor Name, and Mobile Number');
            return;
        }

        setLoading(true);

        try {
            // Validate QR code data
            QRFirebaseUtils.validateQRCodeData(qrData);
            
            const uuid = QRFirebaseUtils.generateUUID();
            const qrCodeData = {
                uuid,
                boxId: qrData.boxId,
                location: qrData.location,
                purpose: qrData.purpose,
                donorName: qrData.donorName,
                donorMobile: qrData.donorMobile,
                donorPan: qrData.donorPan,
                donorEmail: qrData.donorEmail,
                donorAddress: qrData.donorAddress
            };

            // Save to Firebase using utility
            const docId = await QRFirebaseUtils.saveQRCode(qrCodeData);
            console.log('QR Code data saved with ID: ', docId);

            // Generate URL with UUID
            const baseUrl = "https://nimalmaulafoundation.org";
            const url = `${baseUrl}/collection?qr=${uuid}`;
            
            setGeneratedUrl(url);
            setGeneratedUUID(uuid);
            
            toast.success('QR Code generated and saved successfully!');
        } catch (error) {
            console.error('Error generating QR code:', error);
            if (error.message.includes('Missing required fields') || error.message.includes('Invalid')) {
                toast.error(error.message);
            } else {
                toast.error('Error generating QR code. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const downloadQR = () => {
        const canvas = document.querySelector('#qr-canvas canvas');
        if (!canvas) return;
        
        const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `qr-code-${qrData.boxId}-${generatedUUID}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    };

    const resetForm = () => {
        setQrData({
            boxId: '',
            location: '',
            purpose: 'Sadaqah',
            donorName: '',
            donorMobile: '',
            donorPan: '',
            donorEmail: '',
            donorAddress: ''
        });
        setGeneratedUrl('');
        setGeneratedUUID('');
    };

    // Calculate QR code size based on screen size
    const getQRSize = () => {
        if (isSmallMobile) return 200;
        if (isMobile) return 220;
        return 256;
    };

    return (
        <Fragment>
            <Navbar2 Logo={Logo} />
            <PageTitle 
                pageTitle={isMobile ? 'QR Generator' : 'QR Code Generator'} 
                pagesub={isMobile ? 'Generate collection QR codes' : 'Generate QR codes for collection boxes with donor information'} 
            />
            <div className="qr-generator-section section-padding">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className={`col-12 ${isMobile ? 'px-2' : 'col-lg-10'}`}>
                            <Paper 
                                elevation={3} 
                                sx={{ 
                                    p: isMobile ? 2 : 4,
                                    mx: isMobile ? 1 : 0
                                }}
                            >
                                <Typography 
                                    variant={isMobile ? "h5" : "h4"} 
                                    sx={{ 
                                        mb: isMobile ? 2 : 4,
                                        textAlign: 'center',
                                        fontSize: isSmallMobile ? '1.25rem' : 'inherit'
                                    }}
                                >
                                    Collection Box QR Generator
                                </Typography>
                                
                                <Grid container spacing={isMobile ? 2 : 3}>
                                    {/* Collection Box Information */}
                                    <Grid item xs={12}>
                                        <Typography 
                                            variant="h6" 
                                            sx={{ 
                                                mb: 2, 
                                                color: '#4a90e2',
                                                fontSize: isMobile ? '1.1rem' : '1.25rem'
                                            }}
                                        >
                                            Collection Box Information
                                        </Typography>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Box ID *"
                                            name="boxId"
                                            value={qrData.boxId}
                                            onChange={handleInputChange}
                                            placeholder="e.g., BOX001"
                                            variant="outlined"
                                            required
                                            size={isMobile ? "medium" : "medium"}
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Location *"
                                            name="location"
                                            value={qrData.location}
                                            onChange={handleInputChange}
                                            placeholder="e.g., Main Masjid Entrance"
                                            variant="outlined"
                                            required
                                            size={isMobile ? "medium" : "medium"}
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Purpose"
                                            name="purpose"
                                            value={qrData.purpose}
                                            onChange={handleInputChange}
                                            placeholder="e.g., Sadaqah"
                                            variant="outlined"
                                            size={isMobile ? "medium" : "medium"}
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>

                                    {/* Donor Information */}
                                    <Grid item xs={12} sx={{ mt: isMobile ? 2 : 3 }}>
                                        <Typography 
                                            variant="h6" 
                                            sx={{ 
                                                mb: 2, 
                                                color: '#4a90e2',
                                                fontSize: isMobile ? '1.1rem' : '1.25rem'
                                            }}
                                        >
                                            Donor Information
                                        </Typography>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Donor Name *"
                                            name="donorName"
                                            value={qrData.donorName}
                                            onChange={handleInputChange}
                                            placeholder="Enter donor name"
                                            variant="outlined"
                                            required
                                            size={isMobile ? "medium" : "medium"}
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Mobile Number *"
                                            name="donorMobile"
                                            value={qrData.donorMobile}
                                            onChange={handleInputChange}
                                            placeholder="Enter mobile number"
                                            variant="outlined"
                                            required
                                            size={isMobile ? "medium" : "medium"}
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="PAN Number"
                                            name="donorPan"
                                            value={qrData.donorPan}
                                            onChange={handleInputChange}
                                            placeholder="Enter PAN number"
                                            variant="outlined"
                                            size={isMobile ? "medium" : "medium"}
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="donorEmail"
                                            value={qrData.donorEmail}
                                            onChange={handleInputChange}
                                            placeholder="Enter email address"
                                            variant="outlined"
                                            type="email"
                                            size={isMobile ? "medium" : "medium"}
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Address"
                                            name="donorAddress"
                                            value={qrData.donorAddress}
                                            onChange={handleInputChange}
                                            placeholder="Enter complete address"
                                            variant="outlined"
                                            multiline
                                            rows={isMobile ? 2 : 3}
                                            size={isMobile ? "medium" : "medium"}
                                            InputProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                            InputLabelProps={{
                                                style: { fontSize: isMobile ? '16px' : '14px' }
                                            }}
                                        />
                                    </Grid>

                                    {/* Action Buttons */}
                                    <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            flexDirection: isMobile ? 'column' : 'row',
                                            gap: 2,
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={generateQR}
                                                disabled={loading || !qrData.boxId || !qrData.location || !qrData.donorName || !qrData.donorMobile}
                                                size={isMobile ? "large" : "large"}
                                                sx={{
                                                    minHeight: isMobile ? '48px' : '40px',
                                                    fontSize: isMobile ? '16px' : '14px',
                                                    width: isMobile ? '100%' : 'auto',
                                                    maxWidth: isMobile ? '300px' : 'none'
                                                }}
                                            >
                                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Generate QR Code'}
                                            </Button>
                                            
                                            {generatedUrl && (
                                                <Button
                                                    variant="outlined"
                                                    color="secondary"
                                                    onClick={resetForm}
                                                    size={isMobile ? "large" : "large"}
                                                    sx={{
                                                        minHeight: isMobile ? '48px' : '40px',
                                                        fontSize: isMobile ? '16px' : '14px',
                                                        width: isMobile ? '100%' : 'auto',
                                                        maxWidth: isMobile ? '300px' : 'none'
                                                    }}
                                                >
                                                    Reset Form
                                                </Button>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>

                                {generatedUrl && (
                                    <Box sx={{ mt: isMobile ? 3 : 5 }}>
                                        <Typography 
                                            variant="h6" 
                                            sx={{ 
                                                mb: 3, 
                                                textAlign: 'center',
                                                fontSize: isMobile ? '1.1rem' : '1.25rem'
                                            }}
                                        >
                                            Generated QR Code
                                        </Typography>
                                        
                                        <Box sx={{ textAlign: 'center', mb: 3 }} id="qr-canvas">
                                            <QRCodeCanvas
                                                value={generatedUrl}
                                                size={getQRSize()}
                                                bgColor="#ffffff"
                                                fgColor="#000000"
                                                level="L"
                                                includeMargin={true}
                                                style={{ 
                                                    maxWidth: '100%', 
                                                    height: 'auto' 
                                                }}
                                            />
                                        </Box>
                                        
                                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={downloadQR}
                                                size={isMobile ? "large" : "medium"}
                                                sx={{
                                                    minHeight: isMobile ? '48px' : '40px',
                                                    fontSize: isMobile ? '16px' : '14px',
                                                    width: isMobile ? '100%' : 'auto',
                                                    maxWidth: isMobile ? '300px' : 'none'
                                                }}
                                            >
                                                Download QR Code
                                            </Button>
                                        </Box>
                                        
                                        {/* QR Code Information */}
                                        <Paper 
                                            elevation={1} 
                                            sx={{ 
                                                mt: 3, 
                                                p: isMobile ? 2 : 3, 
                                                backgroundColor: '#f9f9f9' 
                                            }}
                                        >
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    mb: 2,
                                                    fontSize: isMobile ? '1.1rem' : '1.25rem'
                                                }}
                                            >
                                                QR Code Details:
                                            </Typography>
                                            
                                            <Grid container spacing={isMobile ? 1 : 2}>
                                                <Grid item xs={12} md={6}>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            mb: 1,
                                                            fontSize: isMobile ? '14px' : '13px',
                                                            lineHeight: 1.5
                                                        }}
                                                    >
                                                        <strong>UUID:</strong> {generatedUUID}
                                                    </Typography>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            mb: 1,
                                                            fontSize: isMobile ? '14px' : '13px',
                                                            lineHeight: 1.5
                                                        }}
                                                    >
                                                        <strong>Box ID:</strong> {qrData.boxId}
                                                    </Typography>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            mb: 1,
                                                            fontSize: isMobile ? '14px' : '13px',
                                                            lineHeight: 1.5
                                                        }}
                                                    >
                                                        <strong>Location:</strong> {qrData.location}
                                                    </Typography>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            mb: 1,
                                                            fontSize: isMobile ? '14px' : '13px',
                                                            lineHeight: 1.5
                                                        }}
                                                    >
                                                        <strong>Purpose:</strong> {qrData.purpose}
                                                    </Typography>
                                                </Grid>
                                                
                                                <Grid item xs={12} md={6}>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            mb: 1,
                                                            fontSize: isMobile ? '14px' : '13px',
                                                            lineHeight: 1.5
                                                        }}
                                                    >
                                                        <strong>Donor:</strong> {qrData.donorName}
                                                    </Typography>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            mb: 1,
                                                            fontSize: isMobile ? '14px' : '13px',
                                                            lineHeight: 1.5
                                                        }}
                                                    >
                                                        <strong>Mobile:</strong> {qrData.donorMobile}
                                                    </Typography>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            mb: 1,
                                                            fontSize: isMobile ? '14px' : '13px',
                                                            lineHeight: 1.5
                                                        }}
                                                    >
                                                        <strong>PAN:</strong> {qrData.donorPan || 'N/A'}
                                                    </Typography>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            mb: 1,
                                                            fontSize: isMobile ? '14px' : '13px',
                                                            lineHeight: 1.5,
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        <strong>Email:</strong> {qrData.donorEmail || 'N/A'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            
                                            <Box sx={{ mt: 2 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ textAlign: 'center' }}
                                                >
                                                    <strong>Collection URL:</strong>
                                                </Typography>
                                                <Box sx={{ 
                                                    mt: 1,
                                                    p: 1,
                                                    backgroundColor: '#fff',
                                                    borderRadius: 1,
                                                    border: '1px solid #ddd'
                                                }}>
                                                    <Typography
                                                        component="code"
                                                        sx={{
                                                            fontSize: isSmallMobile ? '10px' : '12px',
                                                            wordBreak: 'break-all',
                                                            display: 'block',
                                                            textAlign: 'center',
                                                            lineHeight: 1.4
                                                        }}
                                                    >
                                                        {generatedUrl}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Box>
                                )}
                            </Paper>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default QRGenerator;